import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {Tag} from "primereact/tag";
import Avatar from "../../../system/components/avatar";
import useUserEventLog from "../../../models/user-event-log/use-user-event-log";
import {useChatMessage} from "../../../models/chat-message/use-chat-message";
import {useTaskComment} from "../../../models/task-comment/use-task-comment";
import {useTask} from "../../../models/task/use-task";
import {
    entityKey,
    formatEventDate,
    getCategoryMeta,
    includesSearch,
    isIncomingItem,
} from "../inbox-utils";
import ChatComposer from "../chat-composer";
import {useTranslate} from "../../../system/useTranslate";

// goal notifications that ask the owner to act get a highlighted card and a direct button (docs/tasks/17-goal-updates.md)
const GOAL_ACTION_EVENTS = {"goal.period_review": "تعیین تکلیف اهداف", "goal.update_reminder": "بروزرسانی هدف"};

const filters = [
    {label: "همه", value: "all"},
    {label: "منشن‌ها", value: "mention"},
    {label: "کارها", value: "task"},
    {label: "پروژه‌ها", value: "project"},
    {label: "تیمی", value: "team"},
    {label: "گزارش‌ها", value: "report"},
    {label: "پاسخ‌نداده‌ها", value: "unanswered"},
];

// task notifications that can be completed from here (same rule as the push «انجام شد» button)
const DONE_EVENTS = ["task.assigned", "task.due_soon", "task.overdue"];
const canMarkDone = (item) => DONE_EVENTS.includes(item?.eventKey) && item?.sourceType === "task" && Boolean(item?.sourceDocumentId);

function DigestSections({item}) {
    const sections = Array.isArray(item?.payload?.sections) ? item.payload.sections : [];
    if (!sections.length) return null;
    return (
        <div className="grid m-0 mt-2">
            {sections.map((section) => (
                <div key={section.key} className="col-12 md:col-6 p-1">
                    <div className="surface-50 border-round-lg p-2 h-full">
                        <div className="flex align-items-center justify-content-between text-xs mb-1">
                            <strong className="text-700">{section.label}</strong>
                            <span className="text-primary font-bold">
                                {section.key === "task_progress"
                                    ? `${Number(section.count || 0).toLocaleString("fa-IR")} از ${Number(section.total || 0).toLocaleString("fa-IR")}`
                                    : Number(section.count || 0).toLocaleString("fa-IR")}
                            </span>
                        </div>
                        {(section.items || []).slice(0, 3).map((entry) => (
                            <div key={entry.documentId || entry.title} className="text-xs text-600 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                • {entry.title}{entry.project ? ` — ${entry.project}` : ""}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

const sourceOptions = [
    {label: "همه منابع", value: "all"},
    {label: "پروژه‌ها", value: "project"},
    {label: "اهداف", value: "goal"},
    {label: "وظایف", value: "task"},
    {label: "تیم", value: "team"},
    {label: "گفتگوها", value: "conversation"},
];

const timeOptions = [
    {label: "همه زمان‌ها", value: "all"},
    {label: "امروز", value: "today"},
    {label: "دیروز", value: "yesterday"},
    {label: "هفت روز اخیر", value: "week"},
];

const categoryOrder = ["mention", "conversation", "project", "goal", "task", "access", "team", "report", "system"];

function isInTimeRange(value, range) {
    if (range === "all") return true;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    const today = new Date();
    const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const days = Math.round((startToday - startDate) / 86400000);
    if (range === "today") return days === 0;
    if (range === "yesterday") return days === 1;
    return days >= 0 && days <= 7;
}

function openActionUrl(actionUrl) {
    if (!actionUrl) return;
    if (/^https?:\/\//i.test(actionUrl)) {
        window.open(actionUrl, "_blank", "noopener,noreferrer");
        return;
    }
    window.location.hash = actionUrl.replace(/^#/, "");
}

function MentionCard({
    item,
    response,
    pending,
    onResponseChange,
    onUpdate,
    onSubmit,
}) {
    const translate = useTranslate();
    const key = entityKey(item);
    const unanswered = item.requiresResponse && !["responded", "dismissed"].includes(item.responseState);
    const actorName = item.actor?.member?.fullname
        || [item.actor?.member?.firstname, item.actor?.member?.lastname].filter(Boolean).join(" ")
        || item.actor?.member?.username
        || item.title
        || "منشن جدید";
    const sourceParts = [item.sourceLabel, item.contextLabel].filter(Boolean);

    return (
        <article
            key={key}
            className={`surface-card border-round-xl border-1 border-200 border-right-3 border-purple-400 p-3 ${!item.isRead ? "shadow-1" : ""}`}
        >
            <div className="flex flex-wrap align-items-start justify-content-between gap-3">
                <div className="flex align-items-start gap-3 min-w-0">
                    {item.actor ? (
                        <Avatar size="large" memberData={item.actor?.member}/>
                    ) : (
                        <div className="w-3rem h-3rem border-circle flex flex-shrink-0 align-items-center justify-content-center bg-purple-50 text-purple-600">
                            <i className="pi pi-at"/>
                        </div>
                    )}
                    <div className="flex flex-column min-w-0 gap-1">
                        <div className="flex flex-wrap align-items-center gap-2">
                            {!item.isRead && <span className="bg-primary border-circle" style={{width: ".55rem", height: ".55rem"}}/>}
                            <strong className="text-sm text-900">{actorName}</strong>
                            <time className="text-xs text-500">{formatEventDate(item.createdAt)}</time>
                        </div>
                        {!!sourceParts.length && (
                            <div className="flex flex-wrap align-items-center gap-1 text-xs text-500">
                                {sourceParts.map((part, index) => (
                                    <React.Fragment key={`${part}-${index}`}>
                                        {index > 0 && <i className="pi pi-angle-left text-xs"/>}
                                        <span>{part}</span>
                                    </React.Fragment>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {unanswered && (
                    <Tag value="در انتظار پاسخ" severity="warning" icon="pi pi-clock" rounded/>
                )}
                {item.responseState === "responded" && (
                    <Tag value="پاسخ داده شد" severity="success" icon="pi pi-check" rounded/>
                )}
            </div>

            {item.message && (
                <div className="surface-50 border-round-lg p-3 mt-3">
                    <p className="text-xs text-700 line-height-3 white-space-pre-wrap m-0">{item.message}</p>
                </div>
            )}

            {item.responseState === "responded" && item.responseText && (
                <div className="mt-3 border-right-3 border-green-400 bg-green-50 border-round p-2">
                    <small className="block text-green-700 font-medium mb-1">پاسخ شما</small>
                    <span className="text-xs text-700 white-space-pre-wrap">{item.responseText}</span>
                </div>
            )}

            {unanswered && (
                <div className="flex flex-column gap-2 mt-3">
                    <ChatComposer
                        value={response}
                        onChange={(event) => onResponseChange(event.target.value)}
                        placeholder={translate("inbox.composer.reply-placeholder")}
                        showQuickActions={false}
                        onSubmit={onSubmit}
                        submitDisabled={!String(response || "").trim()}
                        loading={pending}
                        disabled={pending}
                    />
                    <div className="flex flex-wrap align-items-center gap-2">
                        <Button
                            label="بعداً جواب می‌دهم"
                            icon="pi pi-clock"
                            severity="secondary"
                            size="small"
                            className="text-xs gap-1"
                            loading={pending}
                            onClick={() => onUpdate({responseState: "later", isRead: true})}
                        />
                        {item.actionUrl && (
                            <Button
                                label="مشاهده کامل"
                                icon="pi pi-external-link"
                                iconPos="right"
                                text
                                size="small"
                                className="text-xs gap-1 mr-auto"
                                onClick={() => openActionUrl(item.actionUrl)}
                            />
                        )}
                    </div>
                </div>
            )}

            {!unanswered && (
                <div className="flex flex-wrap align-items-center gap-2 mt-3">
                    {!item.isRead && (
                        <Button
                            label="خوانده شد"
                            icon="pi pi-check"
                            text
                            size="small"
                            className="text-xs gap-1"
                            loading={pending}
                            onClick={() => onUpdate({isRead: true})}
                        />
                    )}
                    {item.actionUrl && (
                        <Button
                            label="مشاهده کامل"
                            icon="pi pi-external-link"
                            iconPos="right"
                            text
                            size="small"
                            className="text-xs gap-1 mr-auto"
                            onClick={() => openActionUrl(item.actionUrl)}
                        />
                    )}
                </div>
            )}
        </article>
    );
}

function IncomingTab() {
    const translate = useTranslate();
    const location = useLocation();
    const taskApi = useTask();
    const focusKey = useMemo(() => new URLSearchParams(location.search).get("notification"), [location.search]);
    const {inboxItems} = useSelector((state) => state.workspaceGraph);
    const eventApi = useUserEventLog();
    const messageApi = useChatMessage();
    const taskCommentApi = useTaskComment();
    const [activeFilter, setActiveFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [source, setSource] = useState("all");
    const [timeRange, setTimeRange] = useState("all");
    const [responses, setResponses] = useState({});
    const [pendingKey, setPendingKey] = useState(null);

    const groupedItems = useMemo(() => {
        const visible = (inboxItems || [])
            .filter(isIncomingItem)
            .filter((item) => {
                if (activeFilter === "all") return true;
                if (activeFilter === "unanswered") {
                    return item.requiresResponse && !["responded", "dismissed"].includes(item.responseState);
                }
                return item.eventCategory === activeFilter;
            })
            .filter((item) => source === "all" || item.sourceType === source || item.eventCategory === source)
            .filter((item) => isInTimeRange(item.createdAt, timeRange))
            .filter((item) => includesSearch(item, search))
            .sort((left, right) => new Date(right?.createdAt || 0) - new Date(left?.createdAt || 0));

        return categoryOrder.map((category) => ({
            category,
            items: visible.filter((item) => (item.eventCategory || "system") === category),
        })).filter((group) => group.items.length);
    }, [activeFilter, inboxItems, search, source, timeRange]);

    // opened from a push or the bell: bring that notification into view
    useEffect(() => {
        if (!focusKey) return undefined;
        const timer = window.setTimeout(() => {
            document.getElementById(`notification-${focusKey}`)?.scrollIntoView({behavior: "smooth", block: "center"});
        }, 150);
        return () => window.clearTimeout(timer);
    }, [focusKey, inboxItems?.length]);

    const openItem = (item) => {
        if (!item.isRead) eventApi.updateUserEventLog(entityKey(item), {isRead: true}).catch(() => {});
        openActionUrl(item.actionUrl);
    };

    const markTaskDone = async (item) => {
        const key = entityKey(item);
        setPendingKey(key);
        try {
            await taskApi.updateTask(item.sourceDocumentId, {taskStatus: "done", doneAt: Date.now()});
            await eventApi.updateUserEventLog(key, {isRead: true});
        } finally {
            setPendingKey(null);
        }
    };

    const updateItem = async (item, data) => {
        const key = entityKey(item);
        setPendingKey(key);
        try {
            return await eventApi.updateUserEventLog(key, data);
        } finally {
            setPendingKey(null);
        }
    };

    const submitResponse = async (item) => {
        const key = entityKey(item);
        const text = String(responses[key] || "").trim();
        if (!text) return;
        setPendingKey(key);

        try {
            if (item.eventCategory === "mention" && item?.payload?.chatDocumentId) {
                await messageApi.createChatMessage({
                    chat: item.payload.chatDocumentId,
                    messageContent: text,
                    parent: item.payload.messageDocumentId || undefined,
                });
            } else if (
                item.eventCategory === "mention"
                && (
                    item.eventKind === "task.comment.mention.created"
                    || item?.payload?.commentDocumentId
                    || item?.payload?.commentId
                )
            ) {
                const taskIdentifier = item?.payload?.taskDocumentId
                    || item?.payload?.taskId
                    || item.sourceDocumentId;
                if (!taskIdentifier) throw new Error("task-comment.error.task-not-found");
                await taskCommentApi.createTaskComment({
                    task: taskIdentifier,
                    text,
                    parent: item?.payload?.commentDocumentId || item?.payload?.commentId || undefined,
                });
            }

            await eventApi.updateUserEventLog(key, {
                responseText: text,
                responseState: "responded",
                isRead: true,
            });
            setResponses((current) => ({...current, [key]: ""}));
        } finally {
            setPendingKey(null);
        }
    };

    return (
        <div className="h-full min-h-0 overflow-auto p-3 md:p-4">
            <div className="mx-auto" style={{maxWidth: "72rem"}}>
                <div className="surface-card border-round-xl border-1 border-200 p-3 mb-3">
                    <div className="flex flex-wrap align-items-center justify-content-between gap-3">
                        <div className="flex flex-wrap gap-2">
                            {filters.map((filter) => (
                                <Button
                                    key={filter.value}
                                    label={filter.label}
                                    size="small"
                                    outlined={activeFilter !== filter.value}
                                    text={activeFilter !== filter.value}
                                    className="text-xs"
                                    onClick={() => setActiveFilter(filter.value)}
                                />
                            ))}
                        </div>
                        <Button
                            label="همه را خوانده‌شده کن"
                            icon="pi pi-check-circle"
                            text
                            size="small"
                            className="text-xs gap-1"
                            loading={eventApi.loading && pendingKey === null}
                            onClick={() => eventApi.markAllRead()}
                        />
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-top-1 border-100">
                        <IconField className="align-items-center flex w-full md:w-18rem">
                            <InputIcon className="pi pi-search"/>
                            <InputText
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="جست‌وجو در اعلان‌ها..."
                                className="w-full border-round-lg pt-2 pb-2 text-xs"
                            />
                        </IconField>
                        <Dropdown value={source} options={sourceOptions} onChange={(event) => setSource(event.value)} className="w-full sm:w-14rem text-xs"/>
                        <Dropdown value={timeRange} options={timeOptions} onChange={(event) => setTimeRange(event.value)} className="w-full sm:w-12rem text-xs"/>
                    </div>
                </div>

                {!groupedItems.length ? (
                    <div className="surface-card border-round-xl border-1 border-200 flex flex-column align-items-center justify-content-center text-center text-500 p-6" style={{minHeight: "22rem"}}>
                        <i className="pi pi-inbox text-4xl text-300 mb-3"/>
                        <strong className="text-700 text-xs">اعلان تازه‌ای ندارید</strong>
                        <small className="mt-2 text-xs">کارهای واگذارشده، منشن‌ها، سررسیدها، Blockerها و موارد نیازمند پاسخ اینجا جمع می‌شوند.</small>
                    </div>
                ) : groupedItems.map((group) => {
                    const meta = getCategoryMeta(group.category);
                    return (
                        <section key={group.category} className="mb-4">
                            <div className="flex align-items-center gap-2 mb-2 px-1">
                                <div className={`w-2rem h-2rem border-circle flex align-items-center justify-content-center bg-${meta.color}-50 text-${meta.color}-600`}>
                                    <i className={meta.icon}/>
                                </div>
                                <h2 className="m-0 text-sm font-bold">{meta.label}</h2>
                                <Tag value={group.items.length} severity="secondary" rounded/>
                            </div>
                            <div className="flex flex-column gap-2">
                                {group.items.map((item) => {
                                    const key = entityKey(item);
                                    if (item.eventCategory === "mention") {
                                        return (
                                            <MentionCard
                                                key={key}
                                                item={item}
                                                response={responses[key] || ""}
                                                pending={String(pendingKey) === String(key)}
                                                onResponseChange={(value) => setResponses((current) => ({...current, [key]: value}))}
                                                onUpdate={(data) => updateItem(item, data)}
                                                onSubmit={() => submitResponse(item)}
                                            />
                                        );
                                    }
                                    const itemMeta = getCategoryMeta(item.eventCategory);
                                    const unanswered = item.requiresResponse && !["responded", "dismissed"].includes(item.responseState);
                                    return (
                                        <article key={key} id={`notification-${key}`} className={`surface-card border-round-xl border-1 p-3 ${String(focusKey) === String(key) ? "border-primary shadow-2" : GOAL_ACTION_EVENTS[item.eventKey] && !item.isRead ? "border-orange-300 bg-orange-50 shadow-1" : !item.isRead ? "border-primary-200" : "border-200"}`}>
                                            <div className="flex align-items-start gap-3">
                                                {item.actor ? (
                                                    <Avatar size="large" memberData={item.actor?.member}/>
                                                ) : (
                                                    <div className={`w-3rem h-3rem border-circle flex flex-shrink-0 align-items-center justify-content-center bg-${itemMeta.color}-50 text-${itemMeta.color}-600`}>
                                                        <i className={itemMeta.icon}/>
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap align-items-center gap-2">
                                                        {!item.isRead && <span className="bg-primary border-circle" style={{width: ".55rem", height: ".55rem"}}/>}
                                                        <strong className="text-xs">{translate(item.title || item.eventKind || itemMeta.label)}</strong>
                                                        {item.priority && item.priority !== "normal" && (
                                                            <Tag value={item.priority === "urgent" ? "فوری" : "مهم"} severity={item.priority === "urgent" ? "danger" : "warning"}/>
                                                        )}
                                                        {unanswered && <Tag value="نیازمند پاسخ" severity="warning"/>}
                                                        {item.responseState === "responded" && <Tag value="پاسخ داده شد" severity="success" icon="pi pi-check"/>}
                                                        <time className="text-xs text-500 mr-auto">{formatEventDate(item.createdAt)}</time>
                                                    </div>
                                                    {item.actor?.member?.fullname && <div className="text-xs text-500 mt-1">از طرف {item.actor.member.fullname}</div>}
                                                    {item.eventKey === "report.digest"
                                                        ? <DigestSections item={item}/>
                                                        : item.message && <p className="text-xs text-700 line-height-3 mt-2 mb-2 white-space-pre-wrap">{translate(item.message)}</p>}
                                                    {(item.sourceLabel || item.contextLabel) && (
                                                        <div className="surface-50 border-round border-1 border-100 p-2 text-xs text-600 flex align-items-center gap-2">
                                                            <i className={itemMeta.icon}/>
                                                            <span>{[item.sourceLabel, item.contextLabel].filter(Boolean).join(" / ")}</span>
                                                        </div>
                                                    )}

                                                    {item.responseState === "responded" && item.responseText && (
                                                        <div className="mt-3 border-right-3 border-green-400 bg-green-50 border-round p-2">
                                                            <small className="block text-green-700 font-medium mb-1">پاسخ شما</small>
                                                            <span className="text-xs text-700 white-space-pre-wrap">{item.responseText}</span>
                                                        </div>
                                                    )}

                                                    {unanswered && (
                                                        <div className="mt-3 pt-3 border-top-1 border-100">
                                                            <ChatComposer
                                                                value={responses[key] || ""}
                                                                onChange={(event) => setResponses((current) => ({...current, [key]: event.target.value}))}
                                                                placeholder={translate("inbox.composer.reply-placeholder")}
                                                                showQuickActions={false}
                                                                onSubmit={() => submitResponse(item)}
                                                                submitDisabled={!String(responses[key] || "").trim()}
                                                                loading={String(pendingKey) === String(key)}
                                                                disabled={String(pendingKey) === String(key)}
                                                            />
                                                            <div className="flex flex-wrap justify-content-end gap-2 mt-2">
                                                                <Button
                                                                    label="بعداً پاسخ می‌دهم"
                                                                    icon="pi pi-clock"
                                                                    severity="secondary"
                                                                    text
                                                                    size="small"
                                                                    className="text-xs gap-1"
                                                                    loading={String(pendingKey) === String(key)}
                                                                    onClick={() => updateItem(item, {responseState: "later", isRead: true})}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="flex flex-wrap align-items-center gap-2 mt-3">
                                                        {!item.isRead && (
                                                            <Button
                                                                label="خوانده شد"
                                                                icon="pi pi-check"
                                                                size="small"
                                                                text
                                                                className="text-xs gap-1"
                                                                loading={String(pendingKey) === String(key)}
                                                                onClick={() => updateItem(item, {isRead: true})}
                                                            />
                                                        )}
                                                        {canMarkDone(item) && (
                                                            <Button
                                                                label="انجام شد"
                                                                icon="pi pi-check-square"
                                                                size="small"
                                                                outlined
                                                                severity="success"
                                                                className="text-xs gap-1"
                                                                loading={String(pendingKey) === String(key)}
                                                                onClick={() => markTaskDone(item)}
                                                            />
                                                        )}
                                                        {item.actionUrl && GOAL_ACTION_EVENTS[item.eventKey] && (
                                                            <Button
                                                                label={GOAL_ACTION_EVENTS[item.eventKey]}
                                                                icon="pi pi-arrow-left"
                                                                iconPos="right"
                                                                size="small"
                                                                className="text-xs gap-1"
                                                                onClick={() => openItem(item)}
                                                            />
                                                        )}
                                                        {item.actionUrl && item.eventKey !== "report.digest" && !GOAL_ACTION_EVENTS[item.eventKey] && (
                                                            <Button
                                                                label="مشاهده جزئیات"
                                                                icon="pi pi-arrow-left"
                                                                iconPos="right"
                                                                size="small"
                                                                text
                                                                className="text-xs gap-1"
                                                                onClick={() => openItem(item)}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        </section>
                    );
                })}
            </div>
        </div>
    );
}

export default IncomingTab;
